/* eslint-disable @typescript-eslint/no-unused-vars */
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import CurrencyInputMask from 'react-currency-input';

import { translate } from '@portal/services/i18n';
import { formatMoney } from '@portal/services/masks';
import AdvancedAlert from '@portal/components/AdvancedAlert/AdvancedAlert';
import AdvancedDateTimePicker from '@portal/components/AdvancedDateTimePicker/AdvancedDateTimePicker';
import LoanInstallments from '@portal/components/LoanInstallments/LoanInstallments';
import { Tooltip } from '@mui/material';
import useReduxState from '@portal/hooks/useReduxState';
import { Radio } from 'antd';

export interface LoanSimulationProps {
  loanAmount: number;
  setLoanAmount: (value: number) => void;
  alertText: string;
  setAlertText: (text: string) => void;
  maxAmount: number;
  minAmount: number;
  dueDate: DateTime | null;
  setDueDate: (value: DateTime | null) => void;
  minDueDate: DateTime | null;
  selectedInstallment: number | null;
  setSelectedInstallment: (value: number) => void;
  contractStartDate: DateTime | null;
  setContractStartDate: (value: DateTime | null) => void;
  selectedLack?: number | null;
  setSelectedLack: (value: number) => void;
}

const LoanSimulation = ({
  loanAmount,
  setLoanAmount,
  alertText,
  setAlertText,
  maxAmount,
  minAmount,
  dueDate,
  setDueDate,
  selectedInstallment,
  setSelectedInstallment,
  contractStartDate,
  setContractStartDate,
  selectedLack,
  setSelectedLack,
}: LoanSimulationProps) => {
  const {
    loan: { simulations },
  } = useReduxState();

  const maxLacks = simulations?.maxLacks ? simulations?.maxLacks + 1 : 0;

  const lacks = maxLacks
    ? [...Array(maxLacks).keys()].slice(0, selectedInstallment ?? undefined)
    : undefined;

  useEffect(() => {
    if (loanAmount < minAmount) {
      setAlertText(
        `${translate(
          'PAGES.LOAN.SIMULATE.FINANCIAL_DATA.ALERT_TEXTS.MIN_VALUE'
        )}${translate('SHARED.CURRENCY.SYMBOL')}${formatMoney(minAmount)}`
      );
    } else if (loanAmount > maxAmount) {
      setAlertText(
        `${translate(
          'PAGES.LOAN.SIMULATE.FINANCIAL_DATA.ALERT_TEXTS.MAX_VALUE'
        )}${translate('SHARED.CURRENCY.SYMBOL')}${formatMoney(maxAmount)}`
      );
    } else {
      setAlertText('');
    }
  }, [loanAmount, maxAmount, minAmount, setAlertText]);

  useEffect(() => {
    if (contractStartDate) {
      setDueDate(contractStartDate?.plus({ months: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="loan-simulation">
      <div className="loan-simulation__container">
        <div className="loan-simulation__container__alert">
          <AdvancedAlert>
            {translate('PAGES.LOAN.SIMULATE.FINANCIAL_DATA.ALERT')}
          </AdvancedAlert>
        </div>
        <p className="loan-simulation__container__title">
          {translate('PAGES.LOAN.SIMULATE.FINANCIAL_DATA.HOW_MANY')}
        </p>
        <div className="loan-simulation__container__input">
          <CurrencyInputMask
            autoFocus
            value={loanAmount}
            onChange={(_, rawValue) => {
              setLoanAmount(rawValue);
            }}
            thousandSeparator={translate('SHARED.CURRENCY.THOUSAND_SEPARATOR')}
            decimalSeparator={translate('SHARED.CURRENCY.DECIMAL_SEPARATOR')}
            prefix={translate('SHARED.CURRENCY.SYMBOL')}
            className="loan-simulation__container__input__input"
          />
          {alertText ? (
            <p className="loan-simulation__container__input__warn-text">
              {alertText}
            </p>
          ) : null}
        </div>
      </div>

      <div className="loan-simulation__container">
        <p className="loan-simulation__container__title">
          {translate('PAGES.LOAN.SIMULATE.FINANCIAL_DATA.LOAN_RECEIVE.TITLE')}
        </p>
        <Tooltip
          title={translate(
            'PAGES.LOAN.SIMULATE.FINANCIAL_DATA.LOAN_RECEIVE.TOOLTIP'
          )}
        >
          <div className="loan-simulation__container__input">
            <AdvancedDateTimePicker
              inputProps={{
                className:
                  'loan-simulation__container__input__input loan-simulation__container__input__input--date',
              }}
              onlyDate
              value={contractStartDate?.toString()}
              onChange={(value: DateTime) => {
                setDueDate(value.plus({ months: 1 }));
                setContractStartDate(value);
              }}
              maxDate={DateTime.fromISO(
                simulations?.contractStartDate ?? ''
              ).plus({ days: 15 })}
              minDate={DateTime.fromISO(simulations?.contractStartDate ?? '')}
              placeholder={translate(
                'PAGES.LOAN.SIMULATE.FINANCIAL_DATA.LOAN_RECEIVE.PLACEHOLDER'
              )}
              variant="standard"
            />
          </div>
        </Tooltip>
      </div>

      <div className="loan-simulation__container">
        <p className="loan-simulation__container__title">
          {translate('PAGES.LOAN.SIMULATE.FINANCIAL_DATA.DUE_DATE.TITLE')}
        </p>
        <div className="loan-simulation__container__input">
          <AdvancedDateTimePicker
            inputProps={{
              className:
                'loan-simulation__container__input__input loan-simulation__container__input__input--date',
            }}
            onlyDate
            value={dueDate?.toString()}
            onChange={setDueDate}
            maxDate={contractStartDate?.plus({ days: 45 })}
            minDate={contractStartDate?.plus({ days: 15 })}
            placeholder={translate(
              'PAGES.LOAN.SIMULATE.FINANCIAL_DATA.RECEIVE_DATE.PLACEHOLDER'
            )}
            variant="standard"
            disabled={!contractStartDate}
          />
        </div>
      </div>

      <div className="loan-simulation__container">
        {dueDate ? (
          <LoanInstallments
            amount={loanAmount}
            dueDate={dueDate}
            selectedInstallment={selectedInstallment}
            setSelectedInstallment={setSelectedInstallment}
          />
        ) : null}
      </div>

      {selectedInstallment && lacks && !!lacks.length ? (
        <>
          <p className="lacks__title">
            {translate('PAGES.LOAN.SIMULATE.FINANCIAL_DATA.LACKS')}
          </p>
          <Radio.Group className="lacks__radio-container">
            {lacks.map((lack) => (
              <Radio
                value={lack}
                key={lack}
                className="loan-installments__radio-container__radio"
                onChange={() => setSelectedLack(lack)}
              >
                <div className="lacks__radio-container__radio__container">
                  <p className="lacks__radio-container__radio__container__lack">
                    {lack}
                  </p>
                </div>
              </Radio>
            ))}
          </Radio.Group>
        </>
      ) : null}
    </div>
  );
};

export default LoanSimulation;
